import { ActivityIndicator, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { useLocalSearchParams, router } from "expo-router"

import { ar } from "@src/i18n/ar"
import { type RouterOutputs, trpc, formatPrice, sharedStyles, dayjs, formatQty } from "@src/utils"
import { Header, Text, DatePicker, InventoryCollectionPicker, ProductPicker, List } from "@src/components"

type FilterMode = "collection" | "products"
type ReportData = RouterOutputs["inventory"]["report"]

export default function InventoryReport() {
  const storagePlaceId = 1
  const params = useLocalSearchParams<{ from?: string; to?: string; mode?: FilterMode; cId?: string; pIds?: string }>()
  const fromDate = params.from ? dayjs(Number(params.from)).toDate() : dayjs().subtract(30, "day").toDate()
  const toDate = params.to ? dayjs(Number(params.to)).toDate() : dayjs().toDate()
  const filterMode = params.mode ?? "collection"
  const collectionId = params.cId ? Number(params.cId) : undefined
  const productIds = params.pIds ? params.pIds.split(",").filter(Boolean).map(Number) : []

  const report = trpc.inventory.report.useQuery(
    {
      storagePlaceId,
      from: dayjs(fromDate).startOf("day").unix(),
      to: dayjs(toDate).endOf("day").unix(),
      collectionId: filterMode === "collection" ? collectionId : undefined,
      productIds: filterMode === "products" && productIds.length > 0 ? productIds : undefined,
    },
    { enabled: false },
  )

  const canGenerate = (filterMode === "collection" && collectionId !== undefined) || (filterMode === "products" && productIds.length > 0)
  const data = report.data as ReportData | undefined
  const groups = data?.groups ?? []

  const emptyView = () => {
    if (report.isError) {
      return <Text className="text-red-500 text-center py-8">{ar.somethingWentWrong}</Text>
    }

    if (report.isFetched && !report.isFetching && (!data || groups.length === 0)) {
      return <Text className="text-gray-500 text-center py-8">{ar.noReportData}</Text>
    }

    return null
  }

  return (
    <>
      <Header title={ar.inventoryReport} />

      <List
        data={groups}
        keyExtractor={(item) => String(item.base.id)}
        contentContainerStyle={sharedStyles.contentContainer}
        style={{ flex: 1 }}
        renderItem={({ item: g }) => (
          <View className="bg-white rounded-lg p-4 mb-3 border border-gray-200">
            <Text className="text-base font-bold text-gray-900 mb-1">{g.base.name}</Text>
            <Text className="text-xs text-gray-500 mb-3">{g.base.upc}</Text>
            <View className="gap-2">
              <Row label={ar.sellPrice} value={`${formatPrice(g.base.sellingPrice)} ${ar.egp}`} />
              {g.base.lastPurchasePrice !== null && <Row label={ar.lastPurchasePrice} value={`${formatPrice(g.base.lastPurchasePrice)} ${ar.egp}`} />}
              <Row label={ar.averageCost} value={`${formatPrice(g.base.averageCost)} ${ar.egp}`} />
              <View className="h-px bg-gray-200 my-1" />
              <Row label={ar.purchasedQty} value={`${formatQty(g.base.purchased.quantity, g.base.type)} (${formatPrice(g.base.purchased.totalCost)} ${ar.egp})`} />
              <Row
                label={ar.baseSales}
                value={`${formatQty(g.base.sold.quantity, g.base.type)} (${formatPrice(g.base.sold.totalRevenue)} ${ar.egp})`}
                bold
                color="text-green-600"
              />
              <Row
                label={ar.currentStock}
                value={`${formatQty(g.base.inventory.currentQuantity, g.base.type)} (${formatPrice(g.base.inventory.currentValue)} ${ar.egp})`}
                bold
                color="text-blue-600"
              />
            </View>

            {g.derived.length > 0 && (
              <>
                <View className="h-px bg-gray-200 mt-3 mb-2" />
                <Text className="text-sm font-bold text-gray-700 mb-2">{ar.derivedProducts}</Text>
                {g.derived.map((d) => (
                  <View key={d.id} className="gap-1 mb-2 pl-3 border-l-2 border-gray-300">
                    <Text className="text-sm font-medium text-gray-800">{d.name}</Text>
                    <Row label={ar.totalSold} value={`${d.sold.quantity} (${formatPrice(d.sold.totalRevenue)} ${ar.egp})`} />
                    <Row label={ar.purchasedQty} value={`${d.purchased.quantity} (${formatPrice(d.purchased.totalCost)} ${ar.egp})`} />
                  </View>
                ))}

                <View className="h-px bg-gray-200 my-2" />
                <Text className="text-sm font-bold text-gray-700 mb-2">{ar.aggregatedTotal}</Text>
                <View className="gap-2">
                  <Row
                    label={ar.totalSold}
                    value={`${formatQty(g.aggregated.sold.quantity, g.base.type)} (${formatPrice(g.aggregated.sold.totalRevenue)} ${ar.egp})`}
                    bold
                    color="text-green-600"
                  />
                  <Row label={ar.purchasedQty} value={`${formatQty(g.aggregated.purchased.quantity, g.base.type)} (${formatPrice(g.aggregated.purchased.totalCost)} ${ar.egp})`} />
                  <Row
                    label={ar.currentStock}
                    value={`${formatQty(g.aggregated.inventory.currentQuantity, g.base.type)} (${formatPrice(g.aggregated.inventory.currentValue)} ${ar.egp})`}
                    bold
                    color="text-blue-600"
                  />
                </View>
              </>
            )}
          </View>
        )}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        ListEmptyComponent={emptyView}
        ListHeaderComponent={
          <View>
            <View className="mb-4">
              <Text className="text-sm font-bold text-gray-700 mb-2">{ar.selectDateRange}</Text>
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <Text className="text-xs text-gray-500 mb-1">{ar.fromDate}</Text>
                  <DatePicker value={fromDate} onChange={(d) => d && router.setParams({ from: d.getTime().toString() })} />
                </View>
                <View className="flex-1">
                  <Text className="text-xs text-gray-500 mb-1">{ar.toDate}</Text>
                  <DatePicker value={toDate} onChange={(d) => d && router.setParams({ to: d.getTime().toString() })} />
                </View>
              </View>
            </View>

            <View className="mb-4">
              <Text className="text-sm font-bold text-gray-700 mb-2">{ar.selectFilter}</Text>
              <View className="flex-row gap-2">
                <Pressable className={`flex-1 p-3 rounded ${filterMode === "collection" ? "bg-blue-500" : "bg-gray-200"}`} onPress={() => router.setParams({ mode: "collection" })}>
                  <Text className={`text-center text-sm font-bold ${filterMode === "collection" ? "text-white" : "text-gray-700"}`}>{ar.inventoryCollection}</Text>
                </Pressable>
                <Pressable className={`flex-1 p-3 rounded ${filterMode === "products" ? "bg-blue-500" : "bg-gray-200"}`} onPress={() => router.setParams({ mode: "products" })}>
                  <Text className={`text-center text-sm font-bold ${filterMode === "products" ? "text-white" : "text-gray-700"}`}>{ar.products}</Text>
                </Pressable>
              </View>
            </View>

            <View className="mb-4">
              {filterMode === "collection" && (
                <InventoryCollectionPicker
                  value={collectionId !== undefined ? [collectionId] : []}
                  onChange={(ids) => router.setParams({ cId: ids[0] ? ids[0].toString() : "" })}
                />
              )}
              {filterMode === "products" && (
                <ProductPicker
                  value={productIds}
                  onlyBaseProducts
                  onChange={(ids) => router.setParams({ pIds: ids.length > 0 ? ids.join(",") : "" })}
                  types={["STANDARD_PRODUCT", "LOCAL_PRODUCT", "WEIGHT_PRODUCT"]}
                />
              )}
            </View>

            <Pressable className={`p-3 rounded mb-6 ${canGenerate ? "bg-blue-500" : "bg-gray-300"}`} onPress={() => report.refetch()} disabled={!canGenerate || report.isFetching}>
              <View className="flex-row items-center justify-center gap-2">
                {report.isFetching && <ActivityIndicator color="white" size="small" />}
                <Text className="text-white font-bold text-center">{ar.generateReport}</Text>
              </View>
            </Pressable>

            {report.isFetching ? (
              <ActivityIndicator className="py-8" />
            ) : data && groups.length > 0 ? (
              <View className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                <Text className="text-lg font-bold text-gray-900 mb-3">
                  {ar.total} ({groups.length})
                </Text>
                <View className="flex-row flex-wrap gap-3">
                  <StatCard label={ar.purchasedQty} value={data.totals.purchased.quantity.toString()} color="bg-amber-50" />
                  <StatCard label={ar.purchasedCost} value={formatPrice(data.totals.purchased.totalCost) ?? "0"} suffix={ar.egp} color="bg-amber-50" />
                  <StatCard label={ar.totalSold} value={data.totals.sold.quantity.toString()} color="bg-green-50" />
                  <StatCard label={ar.totalRevenue} value={formatPrice(data.totals.sold.totalRevenue) ?? "0"} suffix={ar.egp} color="bg-green-50" />
                  <StatCard label={ar.currentStock} value={data.totals.inventory.currentQuantity.toString()} color="bg-blue-50" />
                  <StatCard label={ar.currentValue} value={formatPrice(data.totals.inventory.currentValue) ?? "0"} suffix={ar.egp} color="bg-blue-50" />
                </View>
              </View>
            ) : null}
          </View>
        }
      />
    </>
  )
}

function Row({ label, value, bold, color }: { label: string; value: string; bold?: boolean; color?: string }) {
  return (
    <View className="flex-row justify-between">
      <Text className="text-sm text-gray-600">{label}</Text>
      <Text className={`text-sm ${bold ? "font-bold" : "font-medium"} ${color ?? ""}`}>{value}</Text>
    </View>
  )
}

function StatCard({ label, value, suffix, color }: { label: string; value: string; suffix?: string; color: string }) {
  return (
    <View className={`${color} rounded-lg p-3 flex-1 min-w-[140]`}>
      <Text className="text-xs text-gray-600 mb-1">{label}</Text>
      <Text className="text-lg font-bold text-gray-900">
        {value} {suffix ? <Text className="text-sm font-normal text-gray-600">{suffix}</Text> : null}
      </Text>
    </View>
  )
}
